import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import {
    Container,
    Paper,
    Stack,
    Title,
    Text,
    Loader,
    Alert,
    MantineProvider,
} from "@mantine/core";
import TransportRouteBookingStepper from "./TransportRouteBookingStepper";
import type { TransportRouteBookingInitResponseDto } from "./types";

const apiUrl = process.env.REACT_APP_API_URL;

export default function TransportRouteBookingPage() {
    const [params] = useSearchParams();

    const eventId = params.get("eventId") ?? "";
    const routeLocationId = params.get("routeLocationId") ?? "";

    const [data, setData] = useState<TransportRouteBookingInitResponseDto | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const run = async () => {
            try {
                setLoading(true);
                setError(null);

                if (!eventId) {
                    setError("Mangler eventId i linket.");
                    setLoading(false);
                    return;
                }

                const res = await axios.get(`${apiUrl}/api/transport-route/booking/init`, {
                    params: {
                        eventId,
                        routeLocationId: routeLocationId || undefined,
                    },
                });

                setData(res.data);
            } catch (e: any) {
                console.error(e);
                setError(e?.response?.data ?? "Kunne ikke indlæse booking.");
            } finally {
                setLoading(false);
            }
        };

        run();
    }, [eventId, routeLocationId]);

    if (loading) {
        return (
            <Container size="sm" py="xl">
                <Paper withBorder p="lg">
                    <Stack>
                        <Loader />
                        <Text>Indlæser…</Text>
                    </Stack>
                </Paper>
            </Container>
        );
    }

    if (error || !data) {
        return (
            <Container size="sm" py="xl">
                <Alert color="red" title="Fejl">
                    {error ?? "Ukendt fejl"}
                </Alert>
            </Container>
        );
    }

    return (
        <Container size="lg" py="xl">
            <Paper withBorder p="lg" radius="md">
                <Stack gap="xs">
                    <Title order={3}>Transport booking</Title>
                    <Text c="dimmed">
                        {data.event.name}
                    </Text>

                    {data.prefillRouteLocationName && (
                        <Text size="sm">
                            Forvalgt pickup: <b>{data.prefillRouteLocationName}</b>
                        </Text>
                    )}
                </Stack>
            </Paper>

            <Paper withBorder p="lg" radius="md" mt="md">
                <MantineProvider
                    theme={{
                        primaryColor: "brand",
                        colors: {
                            brand: [
                                "#ffe5e5",
                                "#ffcaca",
                                "#ff9a9a",
                                "#ff7a7a",
                                "#ff6868",
                                "#ff5e5e",
                                "#f24848",
                                "#e53a3a",
                                "#d92d2d",
                                "#cc2222",
                            ],
                        },
                    }}
                >
                    <TransportRouteBookingStepper init={data} />
                </MantineProvider>
            </Paper>
        </Container>
    );
}